import { supabase } from "@/integrations/supabase/client";
import { CURRENCY_CONSTANTS, formatNaira } from "./currencyUtils";

/**
 * Validates that the sponsor has sufficient wallet balance for stream payment
 * All amounts are in naira (main currency unit)
 */
export async function validateSponsorWalletBalance(
  sponsorId: string,
  requiredAmountNaira: number = CURRENCY_CONSTANTS.STREAM_PAYMENT_NAIRA, // Default: ₦50
): Promise<{ valid: boolean; balance?: number; reason?: string }> {
  try {
    console.log("💰 [WALLET_VALIDATOR] Checking sponsor wallet balance:", {
      sponsorId,
      required_amount_naira: requiredAmountNaira,
      formatted_required: formatNaira(requiredAmountNaira),
    });

    // Use privacy-preserving RPC: returns only boolean, never exposes raw balance.
    // This works under hardened wallets RLS where non-owners cannot read balances.
    const { data: canPay, error: rpcError } = await supabase.rpc(
      "check_sponsor_can_pay_stream",
      {
        p_sponsor_id: sponsorId,
        p_required_amount: requiredAmountNaira,
      },
    );

    if (rpcError) {
      console.error(
        "❌ [WALLET_VALIDATOR] RPC error checking sponsor balance:",
        {
          rpcError,
          sponsorId,
        },
      );
      return { valid: false, reason: "Cannot verify sponsor wallet" };
    }

    if (!canPay) {
      console.log(
        "❌ [WALLET_VALIDATOR] Sponsor cannot afford stream payment:",
        {
          sponsorId,
          required_amount_naira: requiredAmountNaira,
          formatted_required: formatNaira(requiredAmountNaira),
        },
      );
      return {
        valid: false,
        reason: "Sponsor balance is currently insufficient for stream payment",
      };
    }

    console.log("✅ [WALLET_VALIDATOR] Sponsor has sufficient balance");
    return { valid: true };
  } catch (error) {
    console.error("🔥 [WALLET_VALIDATOR] Exception in wallet validation:", {
      error,
      errorMessage: error instanceof Error ? error.message : "Unknown error",
      sponsorId,
    });
    return { valid: false, reason: "System error during wallet validation" };
  }
}
